import type { ReactElement } from 'react';

import { Divider } from '@xing-com/divider';
import {
  IllustrationStickerUp,
  IllustrationStickerCoin,
} from '@xing-com/illustrations';
import { Headline, Hero } from '@xing-com/typography';

import * as Styled from './card-callout.styled';
import type {
  CardCalloutCountdownProps,
  CardCalloutDiscountProps,
  CardCalloutProps,
} from './card-callout.types';

export type { CardCalloutProps } from './card-callout.types';

type CardCalloutComponentProps = CardCalloutProps & {
  id?: string;
};

const CardCalloutDiscount = ({
  eyebrow,
  label,
  value,
}: CardCalloutDiscountProps): ReactElement => (
  <>
    <IllustrationStickerCoin size="medium" aria-hidden="true" />
    <Styled.DiscountBox>
      <Headline as="p" size="small" noMargin>
        {eyebrow}
      </Headline>
      <Hero as="p" size="small" noMargin>
        {value}
      </Hero>
      <Headline as="p" size="large" noMargin>
        {label}
      </Headline>
    </Styled.DiscountBox>
  </>
);

const CardCalloutCountdown = ({
  eyebrow,
  label,
  value,
  countdown,
}: CardCalloutCountdownProps): ReactElement => (
  <>
    <IllustrationStickerCoin size="medium" aria-hidden="true" />
    <Styled.DiscountBox>
      <Styled.CountdownWrapper>
        <Headline as="p" size="small" noMargin>
          {countdown}
        </Headline>
        <Divider />
      </Styled.CountdownWrapper>
      <Headline as="p" size="small" noMargin>
        {eyebrow}
      </Headline>
      <Hero as="p" size="small" noMargin>
        {value}
      </Hero>
      <Headline as="p" size="large" noMargin>
        {label}
      </Headline>
    </Styled.DiscountBox>
  </>
);

const CardCalloutContent = ({ callout }: CardCalloutProps): ReactElement => {
  switch (callout.variant) {
    case 'discount':
      return <CardCalloutDiscount {...callout} />;

    case 'countdown':
      return <CardCalloutCountdown {...callout} />;

    default:
      return <IllustrationStickerUp size="xlarge" aria-hidden="true" />;
  }
};

export const CardCallout = ({
  callout,
  id,
}: CardCalloutComponentProps): ReactElement => (
  <Styled.CalloutWrapper $variant={callout.variant} id={id}>
    <CardCalloutContent callout={callout} />
  </Styled.CalloutWrapper>
);
